import React, { useState } from "react";
import { ref, uploadBytesResumable, getDownloadURL } from "@firebase/storage";
import { storage } from "../../firebase";

interface FileUploaderProps {
  folder?: string; // e.g., "report_sections/aws"
  onUploadComplete: (urls: string[]) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  folder = "report_uploads",
  onUploadComplete,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files);
      setFiles(selected);
      setPreviews(selected.map((file) => URL.createObjectURL(file)));
    }
  };

  const handleRemove = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
  };

  const handleUpload = async () => {
    setUploading(true);
    const urls: string[] = [];

    for (const file of files) {
      const path = `${folder}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);

      const uploadTask = uploadBytesResumable(storageRef, file);

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const percent =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setProgress((prev) => ({
              ...prev,
              [file.name]: percent,
            }));
          },
          (error) => {
            console.error(`Upload failed for ${file.name}`, error);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            urls.push(downloadURL);
            resolve();
          }
        );
      });
    }

    setUploadedUrls(urls);
    onUploadComplete(urls);
    setUploading(false);
  };

  return (
    <div>
      <input
        type="file"
        multiple
        onChange={handleFileChange}
        className="block text-sm text-gray-600 file:mr-4 file:py-2 file:px-4
                   file:rounded-md file:border-0 file:text-sm file:font-semibold
                   file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
      />

      {previews.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4">
          {previews.map((src, index) => (
            <div key={index} className="relative">
              <img
                src={src}
                alt={`preview-${index}`}
                className="w-28 h-28 object-cover rounded border"
              />
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute top-1 right-1 bg-red-600 text-white text-xs rounded-full p-1"
              >
                ✕
              </button>
              {progress[files[index]?.name] !== undefined && (
                <p className="text-xs text-gray-600 mt-1">
                  {progress[files[index].name].toFixed(0)}%
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <button
          onClick={handleUpload}
          disabled={uploading}
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
        >
          {uploading ? "Uploading..." : "Upload Files"}
        </button>
      )}

      {uploadedUrls.length > 0 && (
        <div className="mt-4">
          <p className="text-sm text-green-600">Uploaded Files:</p>
          <ul className="text-sm text-blue-700 underline">
            {uploadedUrls.map((url, i) => (
              <li key={i}>
                <a href={url} target="_blank" rel="noreferrer">
                  {url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
